import type { Point } from "@/lib/geometry";

import type {
  SnapMatch,
  SnapPoint,
  SnapPointKind,
  SnapSegment,
} from "@/features/snapping/types";

interface CellKey {
  col: number;
  row: number;
}

export interface SnapDistanceScale {
  x: number;
  y: number;
}

export interface SnapDistanceLookupOptions {
  scale?: SnapDistanceScale;
}

export interface SnapPointLookupOptions<PointType extends SnapPoint<SnapPointKind>>
  extends SnapDistanceLookupOptions {
  priority?: (point: PointType) => number;
  filter?: (point: PointType) => boolean;
}

export interface SnapSegmentLookupOptions extends SnapDistanceLookupOptions {
  filter?: (point: Point, segment: SnapSegment) => boolean;
  resolvePoint?: (point: Point, segment: SnapSegment) => Point | null;
}

export interface SnapSegmentMatch {
  segment: SnapSegment;
  point: Point;
  distancePx: number;
}

interface IndexedSnapSegment {
  segment: SnapSegment;
  start: Point;
  end: Point;
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const DISTANCE_EPSILON = 0.000001;
const MIN_GRID_CELLS_BEFORE_LINEAR_SCAN = 64;

function keyToString(key: CellKey): string {
  return `${key.col}:${key.row}`;
}

function getCellKey(point: Point, cellSize: number): CellKey {
  return {
    col: Math.floor(point.x / cellSize),
    row: Math.floor(point.y / cellSize),
  };
}

function getResolvedCellSize(cellSize: number): number {
  return Number.isFinite(cellSize) && cellSize > 0 ? cellSize : 1;
}

function getLookupScale(options?: SnapDistanceLookupOptions): SnapDistanceScale {
  const scaleX = options?.scale?.x;
  const scaleY = options?.scale?.y;

  return {
    x: typeof scaleX === "number" && Number.isFinite(scaleX) && scaleX > 0 ? scaleX : 1,
    y: typeof scaleY === "number" && Number.isFinite(scaleY) && scaleY > 0 ? scaleY : 1,
  };
}

function getIndexSearchRadius(radius: number, scale: SnapDistanceScale): number {
  return radius / Math.min(scale.x, scale.y);
}

function shouldUseLinearScan(rings: number, itemCount: number): boolean {
  const searchWidth = rings * 2 + 1;
  const searchedCellCount = searchWidth * searchWidth;
  return (
    searchedCellCount > MIN_GRID_CELLS_BEFORE_LINEAR_SCAN &&
    searchedCellCount > itemCount * 4
  );
}

function pointDistance(a: Point, b: Point, scale: SnapDistanceScale): number {
  const dx = (a.x - b.x) * scale.x;
  const dy = (a.y - b.y) * scale.y;
  return Math.hypot(dx, dy);
}

function isBetterPointMatch<PointType extends SnapPoint<SnapPointKind>>(
  candidate: SnapMatch<PointType>,
  current: SnapMatch<PointType> | null,
  priority?: (point: PointType) => number
): boolean {
  if (!current) {
    return true;
  }
  if (candidate.distancePx < current.distancePx - DISTANCE_EPSILON) {
    return true;
  }
  if (
    !priority ||
    Math.abs(candidate.distancePx - current.distancePx) > DISTANCE_EPSILON
  ) {
    return false;
  }
  return priority(candidate.point) > priority(current.point);
}

function toIndexedSegment(segment: SnapSegment): IndexedSnapSegment | null {
  const start = { x: segment.x1, y: segment.y1 };
  const end = { x: segment.x2, y: segment.y2 };
  if (
    !Number.isFinite(start.x) ||
    !Number.isFinite(start.y) ||
    !Number.isFinite(end.x) ||
    !Number.isFinite(end.y)
  ) {
    return null;
  }

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) {
    return null;
  }

  return {
    segment,
    start,
    end,
    minX: Math.min(start.x, end.x),
    minY: Math.min(start.y, end.y),
    maxX: Math.max(start.x, end.x),
    maxY: Math.max(start.y, end.y),
  };
}

function getSegmentCellKeys(
  segment: IndexedSnapSegment,
  cellSize: number
): string[] {
  const startCell = getCellKey(segment.start, cellSize);
  const endCell = getCellKey(segment.end, cellSize);
  const columnSpan = Math.abs(endCell.col - startCell.col);
  const rowSpan = Math.abs(endCell.row - startCell.row);
  const stepCount = Math.max(columnSpan, rowSpan, 1);
  const keys = new Set<string>();

  for (let step = 0; step <= stepCount; step += 1) {
    const t = step / stepCount;
    keys.add(
      keyToString(
        getCellKey(
          {
            x: segment.start.x + (segment.end.x - segment.start.x) * t,
            y: segment.start.y + (segment.end.y - segment.start.y) * t,
          },
          cellSize
        )
      )
    );
  }

  return Array.from(keys);
}

function findNearestPointOnSegment(
  target: Point,
  indexedSegment: IndexedSnapSegment,
  scale: SnapDistanceScale
): SnapSegmentMatch | null {
  const pageDx = indexedSegment.end.x - indexedSegment.start.x;
  const pageDy = indexedSegment.end.y - indexedSegment.start.y;
  const scaledDx = pageDx * scale.x;
  const scaledDy = pageDy * scale.y;
  const lengthSquared = scaledDx * scaledDx + scaledDy * scaledDy;
  if (lengthSquared <= DISTANCE_EPSILON) {
    return null;
  }

  const targetDx = (target.x - indexedSegment.start.x) * scale.x;
  const targetDy = (target.y - indexedSegment.start.y) * scale.y;
  const rawT = (targetDx * scaledDx + targetDy * scaledDy) / lengthSquared;
  const t = Math.max(0, Math.min(1, rawT));
  const point = {
    x: indexedSegment.start.x + pageDx * t,
    y: indexedSegment.start.y + pageDy * t,
  };

  return {
    segment: indexedSegment.segment,
    point,
    distancePx: pointDistance(target, point, scale),
  };
}

export interface SnapIndex<PointType extends SnapPoint<SnapPointKind> = SnapPoint> {
  /** Coordinates and cell sizes use normalized page units; radius is pixel-scaled. */
  findNearest: (
    target: Point,
    radius: number,
    options?: SnapPointLookupOptions<PointType>
  ) => SnapMatch<PointType> | null;
  size: number;
}

export interface SnapSegmentIndex {
  /** Coordinates and cell sizes use normalized page units; radius and distancePx are pixel-scaled. */
  findNearestPoint: (
    target: Point,
    radius: number,
    options?: SnapSegmentLookupOptions
  ) => SnapSegmentMatch | null;
  size: number;
}

export function buildSnapIndex<PointType extends SnapPoint<SnapPointKind>>(
  points: PointType[],
  cellSize = 0.01
): SnapIndex<PointType> {
  const resolvedCellSize = getResolvedCellSize(cellSize);
  const cells = new Map<string, PointType[]>();

  for (const point of points) {
    const key = getCellKey(point, resolvedCellSize);
    const cellKey = keyToString(key);
    const bucket = cells.get(cellKey);
    if (bucket) {
      bucket.push(point);
    } else {
      cells.set(cellKey, [point]);
    }
  }

  return {
    size: points.length,
    findNearest(target, radius, options) {
      if (!Number.isFinite(radius) || radius <= 0 || points.length === 0) {
        return null;
      }

      const scale = getLookupScale(options);
      const indexRadius = getIndexSearchRadius(radius, scale);
      const center = getCellKey(target, resolvedCellSize);
      const rings = Math.ceil(indexRadius / resolvedCellSize);
      let nearest: SnapMatch<PointType> | null = null;

      const considerPoint = (point: PointType) => {
        if (options?.filter && !options.filter(point)) {
          return;
        }
        const distancePx = pointDistance(target, point, scale);
        if (distancePx > radius) {
          return;
        }
        const match = { point, distancePx };
        if (isBetterPointMatch(match, nearest, options?.priority)) {
          nearest = match;
        }
      };

      if (shouldUseLinearScan(rings, points.length)) {
        for (const point of points) {
          considerPoint(point);
        }
        return nearest;
      }

      for (let dCol = -rings; dCol <= rings; dCol += 1) {
        for (let dRow = -rings; dRow <= rings; dRow += 1) {
          const cellKey = keyToString({ col: center.col + dCol, row: center.row + dRow });
          const bucket = cells.get(cellKey);
          if (!bucket) {
            continue;
          }

          for (const point of bucket) {
            considerPoint(point);
          }
        }
      }

      return nearest;
    },
  };
}

export function buildSnapSegmentIndex(
  segments: SnapSegment[],
  cellSize = 0.02
): SnapSegmentIndex {
  const resolvedCellSize = getResolvedCellSize(cellSize);
  const cells = new Map<string, number[]>();
  const indexedSegments: IndexedSnapSegment[] = [];

  for (const segment of segments) {
    const indexedSegment = toIndexedSegment(segment);
    if (!indexedSegment) {
      continue;
    }

    const segmentIndex = indexedSegments.length;
    indexedSegments.push(indexedSegment);

    for (const cellKey of getSegmentCellKeys(indexedSegment, resolvedCellSize)) {
      const bucket = cells.get(cellKey);
      if (bucket) {
        bucket.push(segmentIndex);
      } else {
        cells.set(cellKey, [segmentIndex]);
      }
    }
  }

  return {
    size: indexedSegments.length,
    findNearestPoint(target, radius, options) {
      if (!Number.isFinite(radius) || radius <= 0 || indexedSegments.length === 0) {
        return null;
      }

      const scale = getLookupScale(options);
      const indexRadius = getIndexSearchRadius(radius, scale);
      const center = getCellKey(target, resolvedCellSize);
      const rings = Math.ceil(indexRadius / resolvedCellSize);
      const visited = new Set<number>();
      let nearest: SnapSegmentMatch | null = null;

      const considerSegment = (segmentIndex: number) => {
        const indexedSegment = indexedSegments[segmentIndex];
        if (
          target.x < indexedSegment.minX - indexRadius ||
          target.x > indexedSegment.maxX + indexRadius ||
          target.y < indexedSegment.minY - indexRadius ||
          target.y > indexedSegment.maxY + indexRadius
        ) {
          return;
        }

        const projectedMatch = findNearestPointOnSegment(
          target,
          indexedSegment,
          scale
        );
        const resolvedPoint = projectedMatch
          ? options?.resolvePoint
            ? options.resolvePoint(
                projectedMatch.point,
                projectedMatch.segment
              )
            : projectedMatch.point
          : null;
        const match =
          projectedMatch && resolvedPoint
            ? {
                ...projectedMatch,
                point: resolvedPoint,
                distancePx: pointDistance(target, resolvedPoint, scale),
              }
            : null;
        if (
          !match ||
          match.distancePx > radius ||
          (options?.filter && !options.filter(match.point, match.segment))
        ) {
          return;
        }
        if (!nearest || match.distancePx < nearest.distancePx) {
          nearest = match;
        }
      };

      if (shouldUseLinearScan(rings, indexedSegments.length)) {
        for (let segmentIndex = 0; segmentIndex < indexedSegments.length; segmentIndex += 1) {
          considerSegment(segmentIndex);
        }
        return nearest;
      }

      for (let dCol = -rings; dCol <= rings; dCol += 1) {
        for (let dRow = -rings; dRow <= rings; dRow += 1) {
          const cellKey = keyToString({ col: center.col + dCol, row: center.row + dRow });
          const bucket = cells.get(cellKey);
          if (!bucket) {
            continue;
          }

          for (const segmentIndex of bucket) {
            if (visited.has(segmentIndex)) {
              continue;
            }
            visited.add(segmentIndex);
            considerSegment(segmentIndex);
          }
        }
      }

      return nearest;
    },
  };
}
